import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Pencil } from 'lucide-react'
import { FormField } from '@/components/templates/entity-form'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { getJSON, patchJSON } from '@/lib/api'
import { canManage, type StaffCapabilities } from '@/pages/staffCapabilities'

type CreditUnitSettings = { credit_unit_settings: { label: string } }

export function CreditUnitSettingsPage({
  capabilities = {},
}: {
  capabilities?: StaffCapabilities
}) {
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const settingsQuery = useQuery({
    queryKey: ['credit-unit-settings'],
    queryFn: () => getJSON<CreditUnitSettings>('/api/v1/credit_unit_settings'),
  })
  const save = useMutation({
    mutationFn: (label: string) =>
      patchJSON<CreditUnitSettings>('/api/v1/credit_unit_settings', {
        credit_unit_settings: { label },
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(['credit-unit-settings'], data)
      setEditing(false)
    },
  })
  const label = settingsQuery.data?.credit_unit_settings.label
  if (settingsQuery.isLoading)
    return <p className="p-6 text-body-sm text-muted-foreground">Loading learning settings…</p>
  if (settingsQuery.isError || label === undefined)
    return <p className="p-6 text-body-sm text-destructive">Learning settings unavailable</p>
  return (
    <div className="p-6">
      <h1 className="mb-4 text-heading-sm font-semibold text-foreground">Learning</h1>
      <Card className="max-w-2xl">
        <CardContent className="p-0">
          <section className="p-4">
            <div className="flex items-center justify-between pb-3">
              <h2 className="text-body font-semibold text-foreground">Credit unit</h2>
              {!editing && canManage(capabilities, 'courses') && (
                <Button
                  aria-label="Edit Credit unit"
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    setDraft(label === 'credits' ? '' : label)
                    setEditing(true)
                  }}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
              )}
            </div>
            {editing ? (
              <form
                className="max-w-sm space-y-4"
                onSubmit={(event) => {
                  event.preventDefault()
                  save.mutate(draft)
                }}
              >
                <FormField label="Label" htmlFor="credit-unit">
                  <Input
                    id="credit-unit"
                    value={draft}
                    onChange={(event) => setDraft(event.target.value)}
                    placeholder="credits"
                  />
                </FormField>
                <div className="flex gap-2">
                  <Button type="submit" disabled={save.isPending}>
                    Save
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setEditing(false)}>
                    Cancel
                  </Button>
                </div>
              </form>
            ) : (
              <div className="flex gap-4 text-body-sm">
                <span className="w-32 shrink-0 text-muted-foreground">Label</span>
                <span className={label === 'credits' ? 'text-muted-foreground' : undefined}>
                  {label}
                </span>
              </div>
            )}
          </section>
        </CardContent>
      </Card>
    </div>
  )
}
